<template>
  <Card :title="getTitle()">
    <p>{{ getDescription() }}</p>
    <CardActionContent :buttons="getButtons()">
      <template #description>
        <CardListContent :list="getList()" />
      </template>
    </CardActionContent>
  </Card>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import Card from "@/components/display/Card";
import CardActionContent from "@/components/display/card-sub-components/CardActionContent";
import CardListContent from "@/components/display/card-sub-components/CardListContent";
import GeneratePassword from "@/components/smart/modals/GeneratePassword";
import NavLink from "@/classes/NavLink";
import store from "@/store";
import ViewPassword from "@/components/smart/modals/ViewPassword";

export default {
  name: "SecureShell",
  components: {
    Card,
    CardActionContent,
    CardListContent,
  },
  props: {
    plan: Object,
    resourceName: String,
    site: Object,
  },
  setup(props, { root }) {
    const user = computed(() => store.getters["remoteUser/getUser"]);
    const status = computed(() => store.getters["remoteUser/getUserStatus"]);
    const PORT = 22;

    const generatePassword = () =>
      store.dispatch("modals/open", {
        template: GeneratePassword,
        data: { resourceName: props.resourceName, site: props.site },
      });

    const getButtons = () => [
      new NavLink(
        root.$t("global.generate-new-password"),
        null,
        generatePassword,
        false,
        "primary",
        "epsilon"
      ),
    ];

    const getCommand = () => {
      const command = isFreeTrial() ? "sftp" : "ssh";
      return getUsername() ? `${command} ${getUsername()}@${props.site.tempDomain}` : undefined;
    };

    const getDescription = () =>
      root.$t(
        `global.${isFreeTrial() ? "credentials-sftp-description" : "credentials-ssh-description"}`
      ) +
      " " +
      root.$t("global.credentials-description");

    const getList = () => {
      let list = [
        { label: root.$t("global.hostname"), content: props.site.tempDomain, canCopy: true },
        { label: root.$t("global.port"), content: PORT, canCopy: true },
        {
          label: root.$t("global.username"),
          content: getUsername(),
          actions: getStatusActions(),
          canCopy: getUsername(),
        },
      ];

      if (getPassword()) {
        list.push({
          label: root.$t("global.password"),
          content: root.$t("global.password-hidden"),
          actions: [
            new NavLink(
              root.$t("global.view-password"),
              "view",
              viewPassword,
              false,
              null,
              "epsilon"
            ),
          ],
        });
      }

      list.push({
        label: root.$t("global.command"),
        content: getCommand(),
        actions: getStatusActions(),
        canCopy: getCommand(),
      });

      return list;
    };

    const getPassword = () => user.value?.password;

    const getStatusActions = () => {
      let actions = [];
      if (isLoading()) {
        actions.push(new NavLink("Loading", "loader", () => null, false, null, "epsilon"));
      } else if (isErrored()) {
        actions.push(new NavLink("Error", "error", () => null, false, null, "tau"));
      }
      return actions;
    };

    const getTitle = () => root.$t(`global.${isFreeTrial() ? "sftp-over-ssh" : "secure-share"}`);

    const getUsername = () => user.value?.username;

    const isErrored = () => status.value.isErrored;
    const isFreeTrial = () => props.plan.package?.isFreeTrial;
    const isLoading = () => status.value.isLoading;

    const viewPassword = () =>
      store.dispatch("modals/open", {
        template: ViewPassword,
        data: { password: getPassword(), service: props.site.domain },
      });

    onMounted(() => {
      store.dispatch("remoteUser/getUser", props.site.id);
    });

    return {
      getButtons,
      getDescription,
      getList,
      getTitle,
      isLoading,
      isErrored,
    };
  },
};
</script>
